import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../loginSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { isDemoUser, resolveGameEntryRoute } from "../../functions/stageReportSync";
import { normalizeThemeHexColor } from "../../functions/themeAssets";
import "../fonts/breuer-headline.css";
import "./login.css";
import { setBackButtonUrl } from "../uiSlice";
import Swal from "sweetalert2";
import { fetchThemeData } from "../../admin/themeSlice";
import {
  validateThemeStageData,
  fireThemeStageDataMissingSwal,
} from "../../functions/validateThemeStageData";

function Login() {
  const dispatch = useDispatch();
  const { status, user, error } = useSelector((state) => state.auth);
  const { status: themeStatus, data: themeData } = useSelector(
    (state) => state.theme
  );

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  const adminToken = useSelector(selectAdminToken);
  const [beginLoading, setBeginLoading] = useState(false);

  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const beginPlayButtonWrapperStyle = useMemo(() => {
    if (!themeData) return { borderRadius: "6px" };

    const buttonColor = normalizeThemeHexColor(themeData?.button_color);
    const textRaw = themeData?.button_Textcolor ?? themeData?.button_text_color;
    const textColor =
      textRaw != null && String(textRaw).trim() !== ""
        ? String(textRaw).trim()
        : undefined;

    const style = { borderRadius: "6px" };
    if (buttonColor) {
      style.background = buttonColor;
    }
    if (textColor) {
      style.color = textColor;
      style.borderColor = textColor;
    }
    return style;
  }, [themeData]);

  const beginPlayButtonTextStyle = useMemo(() => {
    const textRaw = themeData?.button_Textcolor ?? themeData?.button_text_color;
    if (textRaw == null || String(textRaw).trim() === "") return undefined;
    return { color: String(textRaw).trim() };
  }, [themeData]);

  const handleBeginPlay = useCallback(
    async (e) => {
      e.preventDefault();
      if (!user || beginLoading) return;

      let storedUser = user;
      try {
        storedUser = JSON.parse(sessionStorage.getItem("userData") || "{}");
      } catch {
        storedUser = user;
      }

      const isDemoBypass = isDemoUser(storedUser);
      setBeginLoading(true);
      try {
        const [freshTheme, route] = await Promise.all([
          dispatch(fetchThemeData({ themeId: null })).unwrap(),
          resolveGameEntryRoute({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
          }),
        ]);
        const themePayload = freshTheme?.data ?? themeData;
        const stageCheck = validateThemeStageData(themePayload);
        if (!stageCheck.ok) {
          await fireThemeStageDataMissingSwal(stageCheck.missing);
          return;
        }

        navigate(route, { replace: true });
      } catch (err) {
        console.error("handleBeginPlay:", err);
        navigate("/game", { replace: true });
      } finally {
        setBeginLoading(false);
      }
    },
    [user, beginLoading, backendBase, adminToken, navigate, dispatch, themeData]
  );

  useEffect(() => {
    dispatch(loginUser());
  }, [dispatch]);

  useEffect(() => {
    if (status === "succeeded" && user && isAdmin) {
      navigate("/admin");
    }
  }, [status, user, isAdmin, navigate]);

  useEffect(() => {
    if (status === "succeeded" && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  useEffect(() => {
    const body = document.body;
    const keys = [
      "background-attachment",
      "background-size",
      "background-repeat",
      "background-position",
      "overflow-x",
      "margin",
      "padding",
      "height",
      "font-family",
      "backdrop-filter",
    ];
    const snapshot = keys.map((k) => [k, body.style.getPropertyValue(k)]);

    body.style.setProperty("background-attachment", "fixed");
    body.style.setProperty("background-size", "100% 100%");
    body.style.setProperty("background-repeat", "no-repeat");
    body.style.setProperty("background-position", "center");
    body.style.setProperty("overflow-x", "hidden");
    body.style.setProperty("margin", "0");
    body.style.setProperty("padding", "0");
    body.style.setProperty("height", "100vh");
    body.style.setProperty(
      "font-family",
      "'FiraSans-Medium', sans-serif",
      "important"
    );
  

    return () => {
      snapshot.forEach(([k, v]) => {
        if (v) body.style.setProperty(k, v);
        else body.style.removeProperty(k);
      });
    };
  }, []);

 useEffect(() => {
  if (status !== "failed") return;
  const msg = error ? String(error) : "Session expired";
  const redirect = user && user.backButtonRedirect ? user.backButtonRedirect : "/";
  Swal.fire("Session Expired!", msg, "error").then(() => {
    window.location.href = redirect;
  });
}, [status, error, user]);

// show "already played" when login payload has gameover > 0 (DB stages.gameover)
useEffect(() => {
  if (isAdmin) return;
  if (status !== "succeeded" || !user) return;
  if (Number(user.gameover ?? 0) > 0) {
    const msg = error ? String(error) : "You have already played!";
    const redirect = user.backButtonRedirect || "/";
    Swal.fire("You have already played!", "  ", "error").then(() => {
      window.location.href = redirect;
    });
  }
}, [status, user, error, isAdmin]);

  if (status === "loading" || themeStatus === "loading") {
    return (
      <div className="login-main-container">
        <div className="quiz-loader-container">
          <div className="quiz-loader"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-main-container">
      {themeData && (
        <img
          src={process.env.REACT_APP_S3_PATH + themeData.logo}
          className="login-logo1"
          alt="Game Logo"
        />
      )}
        {user && status === "succeeded" && (
        <div style={beginPlayButtonWrapperStyle}>
            <button
              type="button"
              className="begin-play-btn"
              disabled={beginLoading}
              onClick={handleBeginPlay}
              style={beginPlayButtonTextStyle}
            >
              {beginLoading ? "LOADING…" : "BEGIN PLAY"}
            </button>
          </div>
        )}
    </div>
  );
}

export default Login;
